import { useEffect, useState } from "react";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Collapse from "@mui/material/Collapse";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

import { useQuery } from "@tanstack/react-query";
import axios from "axios";

function Sidenav({ color, brand, brandName, routes, user }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;

  const location = useLocation();
  const navigate = useNavigate();
  const collapseName = location.pathname.replace("/", "");

  const checkActive = (pathname, routePath) => {
    if (!routePath) return false;

    const cleanPath = pathname.endsWith("/") && pathname !== "/" ? pathname.slice(0, -1) : pathname;
    const cleanRoute = routePath.endsWith("/") && routePath !== "/" ? routePath.slice(0, -1) : routePath;

    if (cleanPath === cleanRoute || (cleanRoute !== "/" && cleanPath.startsWith(cleanRoute + "/"))) {
      return true;
    }

    const subPageMappings = {
      "/user-list": ["/user/add-user", "/user-edit"],
      "/attendance-list": ["/attendance-details", "/attendance"],
      "/claims/list": ["/claims/add", "/claims/edit"],
      "/leave-list": ["/leave-request/add"],
      "/tickets-list": ["/ticket-add", "/tickets-edit"],
      "/lessons/list": ["/lessons/add", "/lessons/edit", "/lessons/details"],
      "/events/list": ["/events/add", "/events/edit"],
      "/mission-tracker": ["/mission/"],
      "/user-mission": ["/mission/"],
      "/tasks-management": ["/mission-tracker"],
      "/publications/list": ["/publications/add", "/publications/edit"],
      "/work-plan/list": ["/work-plan/add", "/work-plan/edit"],
    };

    const relatedPaths = subPageMappings[cleanRoute];
    if (relatedPaths) {
      return relatedPaths.some((path) => cleanPath.startsWith(path));
    }

    return false;
  };

  const [openMenus, setOpenMenus] = useState({});
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const toggleMenu = (key) => {
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Auto-open collapse if current route matches child
  useEffect(() => {
    routes.forEach((route) => {
      if (route.collapse) {
        const isActive = route.collapse.some((child) => checkActive(location.pathname, child.route));
        if (isActive) {
          setOpenMenus((prev) => ({ ...prev, [route.key]: true }));
        }
      }
    });
  }, [location.pathname, routes]);

  // Responsive sidenav
  useEffect(() => {
    const handleMiniSidenav = () => {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    };
    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, transparentSidenav, whiteSidenav]);

  // Scroll active item into view when route changes
  useEffect(() => {
    const timer = setTimeout(() => {
      const activeItem = document.querySelector(".sidenav-active-item");
      if (activeItem) {
        activeItem.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 100); // Short delay to allow layout transitions
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  // Fetch dynamic forms
  const fetchForms = async () => {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/forms/dropdown/all`, {
      withCredentials: true,
    });
    return res.data.data;
  };

  const { data: formsData } = useQuery({
    queryKey: ["allforms"],
    queryFn: fetchForms,
    retry: false,
  });

  const formRoutes = formsData?.map((form) => ({
    type: "collapse",
    name: form.title,
    key: `form-${form.value}`,
    icon: <Icon fontSize="small">settings</Icon>,
    route: `/mission-tracker/${form.value + "--" + form.title}`,
    showInMenu: true,
    role: [user.role_id],
  }));

  // Append dynamic forms to "Mission Tracker" collapse
  let missionTrackerRoute = null;
  routes.forEach((route) => {
    if (route.key === "mission-tracker") {
      missionTrackerRoute = route;
    } else if (route.collapse) {
      const found = route.collapse.find((c) => c.key === "mission-tracker");
      if (found) missionTrackerRoute = found;
    }
  });

  if (missionTrackerRoute) {
    const existingKeys = new Set((missionTrackerRoute.collapse || []).map((c) => c.key));
    const uniqueFormRoutes = (formRoutes || []).filter((fr) => !existingKeys.has(fr.key));
    missionTrackerRoute.collapse = [...(missionTrackerRoute.collapse || []), ...uniqueFormRoutes];
  }

  const allRoutes = [...routes];

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/logout`, null, {
        withCredentials: true,
      });
      localStorage.removeItem("postLoginRedirect");
      setLogoutOpen(false);
      navigate("/authentication/sign-in");
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const renderRoutes = allRoutes
    .filter((route) => route.key !== "log-out")
    .map((route) => {
      const {
        type,
        name,
        icon,
        title,
        key,
        href,
        route: routePath,
        showInMenu,
        role = [],
        collapse,
      } = route;

      if (!role.includes(user.role_id)) return null;

      if (collapse) {
        return (
          <div key={key}>
            <SidenavCollapse
              name={name}
              icon={icon}
              active={collapse.some((c) => checkActive(location.pathname, c.route))}
              onClick={() => toggleMenu(key)}
              rightIcon={openMenus[key] ? <ExpandLess /> : <ExpandMore />}
            />
            <Collapse in={openMenus[key]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {collapse.map(
                (child) =>
                  child.showInMenu &&
                  child.role?.includes(user.role_id) && (
                    <NavLink key={child.key} to={child.route} style={{ textDecoration: "none" }}>
                      <SidenavCollapse
                        name={child.name}
                        icon={child.icon}
                        active={checkActive(location.pathname, child.route)}
                      />
                    </NavLink>
                  )
              )}
              </List>
            </Collapse>
          </div>
        );
      }

      if (type === "collapse") {
        return href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              name={name}
              icon={icon}
              active={checkActive(location.pathname, routePath)}
            />
          </Link>
        ) : (
          showInMenu && (
            <NavLink key={key} to={routePath} style={{ textDecoration: "none" }}>
              <SidenavCollapse
                name={name}
                icon={icon}
                active={checkActive(location.pathname, routePath)}
              />
            </NavLink>
          )
        );
      }

      if (type === "title") {
        return (
          <MDTypography
            key={key}
            color="text"
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      }

      if (type === "divider") {
        return <Divider key={key} />;
      }

      return null;
    });

  const logoutRoute = allRoutes.find((r) => r.key === "log-out");
  const renderLogout =
    logoutRoute && logoutRoute.role.includes(user.role_id) ? (
      <MDBox
        mt="auto"
        sx={{
          background: "rgba(255,255,255,0.08)",
          mx: 2,
          mb: 2,
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <SidenavCollapse
          key={logoutRoute.key}
          name={logoutRoute.name}
          icon={<Icon sx={{ color: "rgba(255,255,255,0.7) !important" }}>logout</Icon>}
          active={false}
          onClick={() => setLogoutOpen(true)}
          sx={{
            cursor: "pointer",
            margin: "0 !important",
            width: "100% !important",
          }}
        />
      </MDBox>
    ) : null;

  let textColor = "white";
  if (transparentSidenav || (whiteSidenav && !darkMode)) textColor = "dark";
  else if (whiteSidenav && darkMode) textColor = "inherit";

  return (
    <SidenavRoot variant="permanent" ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}>
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox component={NavLink} to={user.role_id === 3 ? "/user-dashboard" : "/dashboard"} display="flex" alignItems="center">
          {brand && <MDBox component="img" src={brand} alt="Brand" width="2.5rem" />}
          <MDBox width={!brandName && "100%"} sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}>
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider />
      <MDBox
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255,255,255,0.5)",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "rgba(255,255,255,0.7)",
          },
        }}
      >
        <List>{renderRoutes}</List>
      </MDBox>

      {renderLogout}

      <Dialog
        open={logoutOpen}
        onClose={() => !isLoggingOut && setLogoutOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 1,
            minWidth: "300px",
          },
        }}
      >
        <DialogTitle>
          <MDTypography variant="h5" fontWeight="medium">
            Confirm Logout
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDTypography variant="body2" color="text">
              Are you sure you want to log out of the CMGFP portal?
            </MDTypography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            variant="text"
            color="secondary"
            onClick={() => setLogoutOpen(false)}
            disabled={isLoggingOut}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="error"
            onClick={handleLogout}
            disabled={isLoggingOut}
            startIcon={isLoggingOut ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isLoggingOut ? "Logging out..." : "Logout"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  user: PropTypes.object.isRequired,
};

export default Sidenav;
